import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { Center } from "../models/center.model";
import { CenterService } from "../services/center.service";
import { CenterActions } from './center.actions';
import { CenterFormFilter } from "../models/center-form-filter.model";
import { patch, updateItem } from "@ngxs/store/operators";
import { CenterData } from "../models/center-data.model";
import { UpdateFormDirty, UpdateFormValue } from "@ngxs/form-plugin";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";

export class CenterStateModel {
    public centersMap: Center[];
    public centers: CenterData[];
    centerForm: {
      model: CenterData
    }
    filters: {
      model: CenterFormFilter;
    };
    public isLoadingCenters: boolean;
    public isListSmaller: boolean;
    public error: string;
  }

  const defaults = {
    centersMap: [],
    centers: null,
    centerForm: {
      model: null,
    },
    filters: {
      model: null,
    },
    isLoadingCenters: false,
    isListSmaller: true,
    error: null
  };

  @State<CenterStateModel>({
    name: 'center',
    defaults,
  })
  @Injectable()
  export class CenterState {
    constructor(
      private alertService: AlertService,
      private centerService: CenterService,
      private localizationService: LocalizationService,
      public store: Store,
    ) {}


  @Action(CenterActions.GetCentersMap)
  getCentersMap (ctx: StateContext<CenterStateModel>, { user, zoneId }: CenterActions.GetCentersMap) {
    return this.centerService.getCentersMap(user, zoneId)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new CenterActions.GetCentersMapSuccess(response)),
        error: (error) =>
          ctx.dispatch(new CenterActions.Error(error, '')),
      });
  }

  @Action(CenterActions.GetCentersMapSuccess)
  getCentersMapSuccess(ctx: StateContext<CenterStateModel>, { centers }: CenterActions.GetCentersMapSuccess) {
    ctx.patchState({
      centersMap: centers.sort((a, b) => a.description.localeCompare(b.description)),
      isLoadingCenters: false,
    });
  }

  @Action(CenterActions.GetCentersByUserAndExerciseMap)
  getCentersByUserAndExerciseMap (ctx: StateContext<CenterStateModel>, { user, exerciseId, filters}: CenterActions.GetCentersByUserAndExerciseMap) {
    return this.centerService.getCentersByUserAndExercise(user, exerciseId, filters)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new CenterActions.GetCentersByUserAndExerciseMapSuccess(response)),
        error: (error) =>
          ctx.dispatch(new CenterActions.Error(error, '')),
      });
  }

  @Action(CenterActions.GetCentersByUserAndExerciseMapSuccess)
  getCentersByUserAndExerciseMapSuccess(ctx: StateContext<CenterStateModel>, { centers }: CenterActions.GetCentersByUserAndExerciseMapSuccess) {
    ctx.patchState({
      centersMap: centers.sort((a, b) => a.description.localeCompare(b.description)),
      isLoadingCenters: false,
    });
  }

  @Action(CenterActions.GetCenters)
  getCenters (ctx: StateContext<CenterStateModel>, { }: CenterActions.GetCenters) {
    const state = ctx.getState();
    ctx.patchState({isLoadingCenters: true});
    return this.centerService.getCentersByFilter(state.filters.model)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new CenterActions.GetCentersSuccess(response)),
        error: (error) =>
          ctx.dispatch(new CenterActions.Error(error, '')),
      });
  }

  @Action(CenterActions.GetCentersSuccess)
  getCentersSuccess(ctx: StateContext<CenterStateModel>, { centers }: CenterActions.GetCentersSuccess) {
    ctx.patchState({
      centers: centers,
      isLoadingCenters: false,
    });
  }

  @Action(CenterActions.GetCenterDetails)
  getReportDetails(ctx: StateContext<CenterStateModel>, { centerCode }: CenterActions.GetCenterDetails
  ) {
    ctx.patchState({ isLoadingCenters: true });
    return this.centerService.getCenterByCenterCode(centerCode).subscribe({
        next: (res) => {
            if(res != undefined){
                ctx.dispatch(new CenterActions.GetCenterDetailsSuccess(res));
            } else {
                ctx.dispatch(new CenterActions.Error('errorElementNotFound', ''));
            }
        },
        error: (error) => {
            ctx.dispatch(new CenterActions.Error(error, ''));
        },
    });
  }

  @Action(CenterActions.GetCenterDetailsSuccess)
  getCenterDetailsSuccess(ctx: StateContext<CenterStateModel>, { center }: CenterActions.GetCenterDetailsSuccess) {
    this.store.dispatch(
      new UpdateFormValue({
        path: 'center.centerForm',
        value: {
          centerCode: center.centerCode,
          oracleAccount: center.oracleAccount == undefined ? "" : center.oracleAccount,
          customerCode: center.customerCode == undefined ? "" : center.customerCode,
          paymentTerms: center.paymentTerms == undefined ? "" : center.paymentTerms,
          paymentMethod: center.paymentMethod == undefined ? "" : center.paymentMethod,
          zoneId: center.zoneId,
          sendIse: center.sendIse,
        }
      })
    );
    this.store.dispatch(
        new UpdateFormDirty({
            dirty: false,
            path: 'center.centerForm'
        })
    );
    ctx.patchState({ isLoadingCenters: false });
  }

  @Action(CenterActions.AddCenter)
  addCenter(ctx: StateContext<CenterStateModel>, {center, user}: CenterActions.AddCenter) {
    this.alertService.confirm(
    this.localizationService.translate("labels.center"),
    this.localizationService.translate('masterForms.centerAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.centerAddConfirm')
    .replace('{0}', center.centerCode)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoadingCenters: true});
        return this.centerService.createCenter(center, user).subscribe({
          next: (res) => {
            ctx.dispatch(new CenterActions.AddCenterSuccess(center))
          },
          error: (error) => {
            ctx.dispatch(new CenterActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(CenterActions.AddCenterSuccess)
  addCenterSuccess(ctx: StateContext<CenterStateModel>, {center}: CenterActions.AddCenterSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      ...state.centers, ...center,
      isLoadingCenters: false,
    });
  }


  @Action(CenterActions.EditCenter)
  editCenter(ctx: StateContext<CenterStateModel>, {center, user}: CenterActions.EditCenter) {
    const state = ctx.getState();
    this.alertService.confirm(
      this.localizationService.translate("labels.center"),
      this.localizationService.translate('masterForms.centerEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.centerEditConfirm')
      .replace('{0}', center.centerCode)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoadingCenters: true});
        return this.centerService.updateCenter(center, user).subscribe({
          next: (res) => ctx.dispatch(new CenterActions.EditCenterSuccess(center)),
          error: (error) => {
            ctx.dispatch(new CenterActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(CenterActions.EditCenterSuccess)
  editCenterSuccess(ctx: StateContext<CenterStateModel>, {center}: CenterActions.EditCenterSuccess) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        centers: updateItem<CenterData>(
          (l) => l.centerCode == center.centerCode, center
        ),
        isLoadingCenters: false
      })
    );
  }


  @Action(CenterActions.Error)
  onErrorOcurred(ctx: StateContext<CenterStateModel>, {error, details}: CenterActions.Error) {
    ctx.patchState({
      error: error,
      isLoadingCenters: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(CenterActions.SetCenterDefaultState)
  setCentersDefaultState(
    ctx: StateContext<CenterStateModel>, {}: CenterActions.SetCenterDefaultState) {
      ctx.setState(
        patch({centerForm : patch({
            model: defaults.centerForm.model
          })
        })
      );
    }

  @Action(CenterActions.SetCenterDefaults)
  setCentersDefaults(
    ctx: StateContext<CenterStateModel>, {}: CenterActions.SetCenterDefaults) {
      ctx.patchState(
        defaults
      );
    }

  private getTitle() {
    return this.localizationService.translate('labels.center');
  }
}
